import { OptJobType, useLatestOptJobAndQueueItemsStatus } from 'api/optJobs/optJobs.api'
import { useSystemFeatureFlagIsActive } from 'utils/systemUtils'
import { mergeOptJobAndQueueItemsByType, OptJobAndQueueItemWithType } from '../../Optimizations.helper'

// The dropdown shows the 50 latest optimizations overall, regardless of type. Each type is
// fetched up to this same cap so the true 50 latest can always be determined after merging. 50 is MAX in backend.
const REVIEW_OPTIMIZATIONS_CAP = 50

export function useOptimizationsReviewJobs(): {
  latestOptJobsAndQueueItems: OptJobAndQueueItemWithType[]
  isFetchingOptJobAndQueueItems: 'idle' | 'pending' | 'success' | 'error'
} {
  const isRegularWipEnabled = useSystemFeatureFlagIsActive('use_draftproductionplan')
  const optJobType: Extract<OptJobType, 'regular' | 'regular_wip'> = isRegularWipEnabled ? 'regular_wip' : 'regular'

  const { data: latestRegularOptJobs = [], status: regularOptJobsStatus } = useLatestOptJobAndQueueItemsStatus({
    optJobType,
    limit: REVIEW_OPTIMIZATIONS_CAP,
  })
  const { data: latestSettlementOptJobs = [], status: settlementOptJobsStatus } = useLatestOptJobAndQueueItemsStatus({
    optJobType: 'settlement',
    limit: REVIEW_OPTIMIZATIONS_CAP,
  })
  const { data: latestFollowupWithDeviationsOptJobs = [], status: followupWithDeviationsOptJobsStatus } =
    useLatestOptJobAndQueueItemsStatus({
      optJobType: 'followup_with_deviations',
      limit: REVIEW_OPTIMIZATIONS_CAP,
    })
  const { data: latestFollowupWithoutDeviationsOptJobs = [], status: followupWithoutDeviationsOptJobsStatus } =
    useLatestOptJobAndQueueItemsStatus({
      optJobType: 'followup_without_deviations',
      limit: REVIEW_OPTIMIZATIONS_CAP,
    })
  const { data: latestFuelPlanOptJobs = [], status: fuelPlanOptJobsStatus } = useLatestOptJobAndQueueItemsStatus({
    optJobType: 'fuel_plan',
    limit: REVIEW_OPTIMIZATIONS_CAP,
  })

  const latestOptJobsAndQueueItems = mergeOptJobAndQueueItemsByType(
    [
      { optJobType, items: latestRegularOptJobs },
      { optJobType: 'settlement', items: latestSettlementOptJobs },
      { optJobType: 'followup_with_deviations', items: latestFollowupWithDeviationsOptJobs },
      { optJobType: 'followup_without_deviations', items: latestFollowupWithoutDeviationsOptJobs },
      { optJobType: 'fuel_plan', items: latestFuelPlanOptJobs },
    ],
    REVIEW_OPTIMIZATIONS_CAP
  )

  const statuses = [
    regularOptJobsStatus,
    settlementOptJobsStatus,
    followupWithDeviationsOptJobsStatus,
    followupWithoutDeviationsOptJobsStatus,
    fuelPlanOptJobsStatus,
  ]
  const isFetchingOptJobAndQueueItems: 'idle' | 'pending' | 'success' | 'error' = statuses.includes('pending')
    ? 'pending'
    : statuses.includes('error')
      ? 'error'
      : 'success'

  return { latestOptJobsAndQueueItems, isFetchingOptJobAndQueueItems }
}
